import React, { useEffect, useRef, useState } from "react";

import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";

import { FieldSchema } from "../../types";
import { InputFactory } from "../input-factory";

interface RepeatedMessageInputProps {
  field: FieldSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  touched?: boolean;
}

export const RepeatedMessageInput: React.FC<RepeatedMessageInputProps> = ({
  field,
  value,
  onChange,
  error,
  touched,
}) => {
  const items = Array.isArray(value)
    ? (value as Array<Record<string, unknown>>)
    : [];

  // Stable per-item keys so React doesn't reuse DOM/state after a removal.
  const nextKeyRef = useRef(items.length);
  const [itemKeys, setItemKeys] = useState<number[]>(() =>
    items.map((_, i) => i),
  );

  // Sync key array when value is externally replaced (e.g. method change / form reset).
  useEffect(() => {
    const count = Array.isArray(value) ? (value as unknown[]).length : 0;
    setItemKeys((prev) => {
      if (prev.length === count) return prev;
      // Keep existing keys for surviving positions; assign fresh keys for any new ones.
      return Array.from({ length: count }, (_, i) => {
        if (i < prev.length) return prev[i];
        return nextKeyRef.current++;
      });
    });
  }, [value]);

  const handleItemFieldChange = (
    index: number,
    fieldName: string,
    fieldValue: unknown,
  ) => {
    const newItems = [...items];
    newItems[index] = { ...(newItems[index] ?? {}), [fieldName]: fieldValue };
    onChange(newItems);
  };

  const handleAddItem = () => {
    setItemKeys((prev) => [...prev, nextKeyRef.current++]);
    onChange([...items, {}]);
  };

  const handleRemoveItem = (index: number) => {
    setItemKeys((prev) => prev.filter((_, i) => i !== index));
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: "600", color: "text.primary", fontSize: "0.9rem" }}
        >
          {field.name}
          {"\u00A0"}(Array)
          {!field.isOptional && " *"}
        </Typography>
        <Chip
          label={`${items.length} item${items.length !== 1 ? "s" : ""}`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontSize: "0.75rem", height: 20 }}
        />
      </Box>

      {field.description && (
        <Typography
          variant="caption"
          sx={{
            mb: 1.5,
            display: "block",
            color: "text.secondary",
            fontSize: "0.8rem",
            lineHeight: 1.4,
          }}
        >
          {field.description}
        </Typography>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {items.map((item, index) => (
          <Box
            key={itemKeys[index]}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 1.5,
              backgroundColor: "background.paper",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: "text.secondary" }}
              >
                Item {index + 1}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleRemoveItem(index)}
                aria-label={`Remove item ${index + 1}`}
                sx={{ color: "text.secondary" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {field.messageFields!.map((nestedField) => (
                <InputFactory
                  key={nestedField.name}
                  field={nestedField}
                  value={(item ?? {})[nestedField.name]}
                  onChange={(fieldName, fieldValue) =>
                    handleItemFieldChange(index, fieldName, fieldValue)
                  }
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={handleAddItem}
        variant="outlined"
        sx={{ mt: items.length > 0 ? 1.5 : 0, textTransform: "none" }}
      >
        Add item
      </Button>

      {error && touched && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 1, display: "block" }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};
